const scalarType = '';

/** Desplazamiento en +Z (delante del jugador, mirando al sur por defecto). */
export const FRONT_Z = 4;

/**
 * Convierte una coordenada Z relativa (~, ~-1, ~2...) para colocarla delante del jugador.
 */
export const frontZCoord = coord => {
    const s = String(coord);
    if (!s.startsWith('~')) return textInput(s);
    if (s === '~') return textInput(`~${FRONT_Z}`);
    const offset = parseInt(s.slice(1), 10);
    if (Number.isNaN(offset)) return textInput(`~${FRONT_Z}`);
    return textInput(`~${FRONT_Z + offset}`);
};

export const frontSetBlock = (blockName, startX, startY, startZ) =>
    setBlock(blockName, startX, startY, frontZCoord(startZ));

export const frontSetBlocks = (blockName, startX, startY, startZ, endX, endY, endZ) =>
    setBlocks(
        blockName,
        startX,
        startY,
        frontZCoord(startZ),
        endX,
        endY,
        frontZCoord(endZ)
    );

export const frontSpawnEntity = (entityName, startX, startY, startZ) =>
    spawnEntity(entityName, startX, startY, frontZCoord(startZ));

export const frontSpawnParticle = (particleName, startX, startY, startZ, endX, endY, endZ, speed, count) =>
    spawnParticle(
        particleName,
        startX,
        startY,
        frontZCoord(startZ),
        endX,
        endY,
        frontZCoord(endZ),
        speed,
        count
    );

export const frontSearchBlock = (startX, startY, startZ) =>
    searchBlock(startX, startY, frontZCoord(startZ));

/** Z relativa que avanza delante del jugador usando una variable (paso 0,1,2...). */
export const frontVariableZ = variableName =>
    join('~', add(variableReporter(variableName), numberInput(FRONT_Z)));

/** Prefijo legacy por defecto: los ejemplos no deben cargar la extension moderna encima. */
const LEGACY_EXT = 'RaspberryJamMod';

export const connect = (host = 'localhost') => ({
    opcode: `${LEGACY_EXT}_connect_p`,
    inputs: {
        ip: textInput(host)
    }
});

/** Prefijo comun: conectar antes de construir (sin bloque moderno getPlayerPosition). */
export const connectAndBuild = blocks => [
    connect(),
    ...blocks
];

const makeShadowInput = (shadowType, fieldName, value) => ({
    kind: 'shadow',
    shadowType,
    fieldName,
    value: String(value)
});

export const textInput = value => makeShadowInput('text', 'TEXT', value);
export const numberInput = value => makeShadowInput('math_number', 'NUM', value);
export const menuInput = (menuName, value) => makeShadowInput(`minecraft_menu_${menuName}`, menuName, value);
export const stackInput = blocks => ({
    kind: 'stack',
    blocks
});

export const variableField = (name, scope = 'local', type = scalarType) => ({
    kind: 'variable',
    name,
    scope,
    type
});

export const variableReporter = (name, scope = 'local', type = scalarType) => ({
    opcode: 'data_variable',
    fields: {
        VARIABLE: variableField(name, scope, type)
    }
});

export const listReporter = (name, scope = 'local') => ({
    opcode: 'data_listcontents',
    fields: {
        LIST: variableField(name, scope, 'list')
    }
});

export const script = (x, y, blocks) => ({
    x,
    y,
    blocks
});

export const greenFlagScript = (x, y, blocks) => script(x, y, [
    {opcode: 'event_whenflagclicked'},
    ...blocks
]);

export const join = (left, right) => ({
    opcode: 'operator_join',
    inputs: {
        STRING1: left,
        STRING2: right
    }
});

export const joinAll = (...parts) => parts.reduce((combined, part) => {
    if (typeof combined === 'undefined') return part;
    return join(combined, part);
}, undefined);

export const add = (left, right) => ({
    opcode: 'operator_add',
    inputs: {
        NUM1: left,
        NUM2: right
    }
});

export const subtract = (left, right) => ({
    opcode: 'operator_subtract',
    inputs: {
        NUM1: left,
        NUM2: right
    }
});

export const multiply = (left, right) => ({
    opcode: 'operator_multiply',
    inputs: {
        NUM1: left,
        NUM2: right
    }
});

export const equals = (left, right) => ({
    opcode: 'operator_equals',
    inputs: {
        OPERAND1: left,
        OPERAND2: right
    }
});

export const greaterThan = (left, right) => ({
    opcode: 'operator_gt',
    inputs: {
        OPERAND1: left,
        OPERAND2: right
    }
});

export const lessThan = (left, right) => ({
    opcode: 'operator_lt',
    inputs: {
        OPERAND1: left,
        OPERAND2: right
    }
});

export const relativeFromVariable = variableName => join('~', variableReporter(variableName));
export const relativeNegativeFromVariable = variableName => join('~-', variableReporter(variableName));

export const say = message => ({
    opcode: 'looks_say',
    inputs: {
        MESSAGE: message
    }
});

export const sayForSeconds = (message, seconds) => ({
    opcode: 'looks_sayforsecs',
    inputs: {
        MESSAGE: message,
        SECS: seconds
    }
});

export const wait = seconds => ({
    opcode: 'control_wait',
    inputs: {
        DURATION: seconds
    }
});

export const repeat = (times, blocks) => ({
    opcode: 'control_repeat',
    inputs: {
        TIMES: times,
        SUBSTACK: stackInput(blocks)
    }
});

export const repeatUntil = (condition, blocks) => ({
    opcode: 'control_repeat_until',
    inputs: {
        CONDITION: condition,
        SUBSTACK: stackInput(blocks)
    }
});

export const ifBlock = (condition, blocks) => ({
    opcode: 'control_if',
    inputs: {
        CONDITION: condition,
        SUBSTACK: stackInput(blocks)
    }
});

export const ifElse = (condition, thenBlocks, elseBlocks) => ({
    opcode: 'control_if_else',
    inputs: {
        CONDITION: condition,
        SUBSTACK: stackInput(thenBlocks),
        SUBSTACK2: stackInput(elseBlocks)
    }
});

export const setVariable = (name, value, scope = 'local') => ({
    opcode: 'data_setvariableto',
    fields: {
        VARIABLE: variableField(name, scope, scalarType)
    },
    inputs: {
        VALUE: value
    }
});

export const changeVariable = (name, value, scope = 'local') => ({
    opcode: 'data_changevariableby',
    fields: {
        VARIABLE: variableField(name, scope, scalarType)
    },
    inputs: {
        VALUE: value
    }
});

export const getPosX = () => ({opcode: 'minecraft_getPosX'});
export const getPosY = () => ({opcode: 'minecraft_getPosY'});
export const getPosZ = () => ({opcode: 'minecraft_getPosZ'});
export const getSearchedBlock = () => ({opcode: 'minecraft_getSearchedBlock'});

export const chat = text => ({
    opcode: `${LEGACY_EXT}_chat`,
    inputs: {
        msg: text
    }
});

export const setBlock = (blockName, startX, startY, startZ) => ({
    opcode: `${LEGACY_EXT}_setBlock`,
    inputs: {
        b: blockName,
        x: startX,
        y: startY,
        z: startZ
    }
});

export const setBlocks = (blockName, startX, startY, startZ, endX, endY, endZ) => ({
    opcode: 'minecraft_setBlocks',
    inputs: {
        BLOCK: blockName,
        STARTX: startX,
        STARTY: startY,
        STARTZ: startZ,
        ENDX: endX,
        ENDY: endY,
        ENDZ: endZ
    }
});

export const resetAroundHere = area => ({
    opcode: 'minecraft_resetAroundHere',
    inputs: {
        AREA: area
    }
});

export const spawnEntity = (entityName, startX, startY, startZ) => ({
    opcode: `${LEGACY_EXT}_spawnEntity`,
    inputs: {
        entity: entityName,
        x: startX,
        y: startY,
        z: startZ
    }
});

export const getPlayerPosition = () => ({
    opcode: 'minecraft_getPlayerPosition'
});

export const teleport = (startX, startY, startZ) => ({
    opcode: `${LEGACY_EXT}_setPlayerPos`,
    inputs: {
        x: startX,
        y: startY,
        z: startZ
    }
});

export const searchBlock = (startX, startY, startZ) => ({
    opcode: 'minecraft_searchBlock',
    inputs: {
        STARTX: startX,
        STARTY: startY,
        STARTZ: startZ
    }
});

export const checkBlockType = blockName => ({
    opcode: 'minecraft_checkBlockType',
    inputs: {
        TARGETBLOCK: blockName
    }
});

export const changeWeather = weatherIndex => ({
    opcode: 'minecraft_changeWeather',
    inputs: {
        WEATHER: menuInput('WEATHER', weatherIndex)
    }
});

export const changeGameMode = gameModeIndex => ({
    opcode: 'minecraft_changeGameMode',
    inputs: {
        GAMEMODE: menuInput('GAMEMODE', gameModeIndex)
    }
});

export const changeDifficulty = difficultyIndex => ({
    opcode: 'minecraft_changeDifficulty',
    inputs: {
        DIFFICULTY: menuInput('DIFFICULTY', difficultyIndex)
    }
});

export const giveEnchant = (levelIndex, enchantIndex) => ({
    opcode: 'minecraft_giveEnchant',
    inputs: {
        LEVEL: menuInput('LEVEL', levelIndex),
        ENCHANT: menuInput('ENCHANT', enchantIndex)
    }
});

export const spawnParticle = (particleName, startX, startY, startZ, endX, endY, endZ, speed, count) => ({
    opcode: 'minecraft_spawnParticle',
    inputs: {
        PARTICLE: particleName,
        STARTX: startX,
        STARTY: startY,
        STARTZ: startZ,
        ENDX: endX,
        ENDY: endY,
        ENDZ: endZ,
        SPEED: speed,
        COUNT: count
    }
});

export const turtleMove = (move, steps) => ({
    opcode: 'minecraft_turtleMove',
    inputs: {
        MOVE: move,
        STEPS: steps
    }
});

export const turtleTurn = (rotate, degrees) => ({
    opcode: 'minecraft_turtleTurn',
    inputs: {
        ROTATE: rotate,
        DEGREES: degrees
    }
});

